import { makeObservable, observable } from "mobx";
import { log } from "@/lib/log";
import type { RootStore } from "@/store";
import { PingResponse } from "@common/api/ping";
import { fetchPing, PingApiArgs } from "../api/ping";
import { GuideRegistry } from "@/stores/GuideRegistry";
import { ChecklistRegistry } from "@/stores/ChecklistRegistry";

type SyncGuidesArgs = Omit<PingApiArgs, "appId" | "apiBaseUrl">;

type SyncGuidesRequest = {
  args: SyncGuidesArgs;
  resolve: (guides: SyncResult | undefined) => void;
  reject: (reason?: any) => void;
};

export type SyncResult = {
  allGuides: PingResponse["allGuides"];
  allChecklists: PingResponse["allChecklists"];
  userState: Record<string, any>;
  debugData: PingResponse["debug"];
};

interface PyneClient {
  currentUserData: Record<string, any>;
}

export interface SyncManagerParams {
  rootStore: RootStore;
  pyneClient: PyneClient;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
}

export class SyncManager {
  rootStore: RootStore;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
  private _syncMeta?: { lastArguments: any; lastFetch: number };

  requestQueue: SyncGuidesRequest[] = [];
  processingQueue = false; // Track if a request is being processed
  private _currentRequestId = 0;
  private pyneClient: PyneClient;
  private lastUserState: Record<string, any> = {};
  private lastDebugData: PingResponse["debug"];

  // For easy mocking
  fetchPing = fetchPing;

  constructor({ rootStore, pyneClient, guideRegistry, checklistRegistry }: SyncManagerParams) {
    makeObservable(this, {
      requestQueue: observable.ref,
      processingQueue: observable.ref,
    });
    this.rootStore = rootStore;
    this.pyneClient = pyneClient;
    this.guideRegistry = guideRegistry;
    this.checklistRegistry = checklistRegistry;
  }

  get pyneSettings() {
    return this.rootStore.pyneSettings;
  }

  get isSettled() {
    return !this.processingQueue && this.requestQueue.length === 0;
  }

  /**
   * Queue and process the sync guides request
   */
  async syncGuides({
    previewGuideId,
    previewChecklistId,
    activeGuideId,
    tourPlaybackVersion,
  }: {
    previewGuideId?: string;
    previewChecklistId?: string;
    activeGuideId?: string;
    tourPlaybackVersion?: string;
  }): Promise<SyncResult | undefined> {
    log("syncGuides called");
    const userData = this.pyneClient.currentUserData;
    return new Promise((resolve, reject) => {
      this._currentRequestId++;
      this.requestQueue.push({
        args: {
          requestId: this._currentRequestId,
          userData,
          previewGuideId,
          previewChecklistId,
          activeGuideId,
          activeTourPlaybackVersion: tourPlaybackVersion,
        },
        resolve,
        reject,
      });
      this.processNextRequest();
    });
  }

  /**
   * Process the next request in the queue
   */
  private async processNextRequest() {
    if (this.processingQueue || this.requestQueue.length === 0) {
      return; // Already processing or queue is empty
    }

    this.processingQueue = true;

    const { args, resolve, reject } = this.requestQueue.shift()!;

    try {
      const syncMeta = {
        lastArguments: args.userData,
        lastFetch: Date.now(),
      };

      const isSamePayload = JSON.stringify(this._syncMeta?.lastArguments) === JSON.stringify(syncMeta.lastArguments);
      const isStale = this._syncMeta?.lastFetch && Date.now() - this._syncMeta.lastFetch > 1000 * 10; // 10 seconds
      if (isSamePayload && !isStale) {
        log("sync skipped");
        resolve({
          userState: this.lastUserState,
          allGuides: this.guideRegistry.guides,
          allChecklists: this.checklistRegistry.checklists,
          debugData: this.lastDebugData,
        });
      } else {
        this._syncMeta = syncMeta;
        log("syncing");

        if (!(this.pyneSettings.app_id && this.pyneSettings.api_base)) {
          throw new Error("Pyne settings not found");
        }

        const result = await this.fetchPing({
          appId: this.pyneSettings.app_id,
          apiBaseUrl: this.pyneSettings.api_base,
          ...args,
        });

        this.lastUserState = result.userState;
        this.lastDebugData = result.debugData;
        this.guideRegistry.setGuides(result.allGuides);
        this.checklistRegistry.setChecklists(result.allChecklists);

        resolve(result);
      }
    } catch (error) {
      reject(error);
    } finally {
      this.processingQueue = false;
      this.processNextRequest();
    }
  }
}
